/**
 * <ry-icon>
 *
 * Renders an icon from the icon registry.
 *
 * Usage:
 * <ry-icon name="close"></ry-icon>
 * <ry-icon name="check" size="16"></ry-icon>
 * <ry-icon name="heart" size="32"></ry-icon>
 *
 * Attributes:
 * - name: icon name from registry (required)
 * - size: icon size in pixels (default: 24)
 * - label: accessible label (for screen readers)
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyIcon extends RyElement {
  static get observedAttributes(): string[] {
    return ['name', 'size', 'label'];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue && this.hasAttribute('data-ry-initialized')) {
      this.#render();
    }
  }

  #render(): void {
    const name = this.getAttribute('name') ?? '';
    const size = this.getAttribute('size') ?? '24';
    const label = this.getAttribute('label');

    const svg = getIcon(name);

    if (!svg) {
      this.innerHTML = '';
      return;
    }

    // Inject SVG and update size
    this.innerHTML = svg;

    const svgEl = this.querySelector('svg');
    if (svgEl) {
      svgEl.setAttribute('width', size);
      svgEl.setAttribute('height', size);

      // Accessibility
      if (label) {
        svgEl.setAttribute('role', 'img');
        svgEl.setAttribute('aria-label', label);
      } else {
        svgEl.setAttribute('aria-hidden', 'true');
      }
    }
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    this.setAttribute('name', value);
  }

  get size(): number {
    return parseInt(this.getAttribute('size') ?? '24', 10);
  }

  set size(value: number) {
    this.setAttribute('size', String(value));
  }
}

customElements.define('ry-icon', RyIcon);
